import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { StatusBadge, getStatusDisplay, normalizeStatus } from "@/components/ui/status-badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, AlertCircle, Calendar, Clock, FileText } from "lucide-react";
import { format, isBefore, isAfter, addDays, differenceInDays } from "date-fns";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { fetchDepartments, Department } from "@/lib/department-utils";
import { PolicyLibraryCard } from "@/components/policy/ResponsivePolicyCard";
import { PolicyCardSkeleton } from "@/components/ui/policy-card-skeleton";

interface Policy {
  id: string;
  title: string;
  department: string | null;
  description: string | null;
  version: number;
  status: 'draft' | 'under_review' | 'pending_approval' | 'approved' | 'rejected';
  created_by: string;
  created_at: string;
  updated_at: string;
  effective_date: string | null;
  next_renewal_date: string | null;
  creator: {
    name: string;
  } | null;
}

interface ExpiredRenewalPolicy extends Policy {
  expiryReason: 'expired_condition' | 'renewal_due';
  expiryDate: string;
  daysUntilExpiry: number;
}

const PolicyExpiredRenewals = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [expiryFilter, setExpiryFilter] = useState("all");
  const [policies, setPolicies] = useState<ExpiredRenewalPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState<Department[]>([]);
  const { profile, hasAnyRole, roles } = useAuth();
  const { toast } = useToast();

  const AlertIcon = AlertCircle;

  useEffect(() => {
    fetchPolicies();
  }, [profile?.company_id]);

  useEffect(() => {
    if (profile?.company_id) {
      fetchDepartments(profile.company_id)
        .then(setDepartments)
        .catch(console.error);
    }
  }, [profile?.company_id]);

  const fetchPolicies = async () => {
    if (!profile?.company_id) return;
    
    setLoading(true);
    try {
      // Fetch all policies
      let query = supabase
        .from('policies')
        .select('*')
        .eq('company_id', profile.company_id);

      if (hasAnyRole(['admin', 'creator'])) {
        // Admins and creators can see all policies
        query = query;
      } else if (hasAnyRole(['reviewer', 'approver'])) {
        query = query.or(
          `status.eq.approved,` +
          `and(status.neq.draft,assigned_reviewers.cs.{${profile.id}}),` +
          `and(status.neq.draft,assigned_approvers.cs.{${profile.id}})`
        );
      } else {
        query = query.eq('status', 'approved');
      }

      const { data: policiesData, error: policiesError } = await query.order('created_at', { ascending: false });

      if (policiesError) throw policiesError;

      if (!policiesData) {
        setPolicies([]);
        setLoading(false);
        return;
      }

      // Fetch approval conditions
      const { data: conditionsData, error: conditionsError } = await supabase
        .from('approval_conditions')
        .select('*')
        .eq('company_id', profile.company_id)
        .eq('is_approved', false);

      if (conditionsError) throw conditionsError;

      // Get creator profiles
      const creatorIds = [...new Set(policiesData.map(policy => policy.created_by))];
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, name')
        .in('id', creatorIds);

      if (profilesError) throw profilesError;

      // Combine policies with creator information
      const policiesWithCreators = policiesData.map(policy => ({
        ...policy,
        creator: profilesData?.find(profile => profile.id === policy.created_by) || null
      }));

      // Process policies to find expired conditions and upcoming renewals
      const now = new Date();
      const oneMonthFromNow = addDays(now, 30);
      const processedPolicies: ExpiredRenewalPolicy[] = [];

      policiesWithCreators.forEach(policy => {
        // Check for expired approval conditions
        const policyConditions = conditionsData?.filter(c => c.policy_id === policy.id) || [];
        const expiredConditions = policyConditions.filter(condition => {
          if (condition.expiry_date) {
            return isBefore(new Date(condition.expiry_date), now);
          }
          return false;
        });

        // Check for upcoming renewals (within next month)
        const renewalDue = policy.next_renewal_date && 
                          isBefore(new Date(policy.next_renewal_date), oneMonthFromNow) &&
                          isAfter(new Date(policy.next_renewal_date), now);

        if (expiredConditions.length > 0) {
          // Find the most recent expiry date
          const expiryDate = expiredConditions
            .map(c => new Date(c.expiry_date!))
            .sort((a, b) => b.getTime() - a.getTime())[0];

          processedPolicies.push({
            ...policy,
            expiryReason: 'expired_condition',
            expiryDate: expiryDate.toISOString(),
            daysUntilExpiry: -1 * differenceInDays(now, expiryDate)
          });
        } else if (renewalDue && policy.next_renewal_date) {
          const renewalDate = new Date(policy.next_renewal_date);
          processedPolicies.push({
            ...policy,
            expiryReason: 'renewal_due',
            expiryDate: policy.next_renewal_date,
            daysUntilExpiry: differenceInDays(renewalDate, now)
          });
        }
      });

      setPolicies(processedPolicies);
    } catch (error) {
      console.error('Error fetching policies:', error);
      toast({
        title: "Error",
        description: "Failed to fetch policies",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getDepartmentDisplay = (department: string | null) => {
    if (!department) return 'General';
    return department;
  };

  const filteredPolicies = policies.filter(policy => {
    const matchesSearch = policy.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (policy.description && policy.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesDepartment = departmentFilter === "all" || policy.department === departmentFilter;
    
    let matchesExpiry = true;
    if (expiryFilter === "expired") {
      matchesExpiry = policy.expiryReason === 'expired_condition';
    } else if (expiryFilter === "renewal") {
      matchesExpiry = policy.expiryReason === 'renewal_due';
    }
    
    return matchesSearch && matchesDepartment && matchesExpiry;
  });

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <AlertIcon className="w-8 h-8 text-orange-600" />
          <div>
            <h1 className="text-3xl font-medium text-foreground">Expired Conditions & Renewals</h1>
            <p className="text-muted-foreground">
              Policies with expired approval conditions or upcoming renewals
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="shadow-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search policies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={expiryFilter} onValueChange={setExpiryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="expired">Expired Conditions</SelectItem>
                <SelectItem value="renewal">Upcoming Renewals</SelectItem>
              </SelectContent>
            </Select>

            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept.id} value={dept.name}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Policy Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <PolicyCardSkeleton key={i} />
          ))
        ) : (
          filteredPolicies.map((policy) => (
            <Card key={policy.id} className="shadow-card hover:shadow-elevated transition-shadow relative">
              {policy.expiryReason === 'expired_condition' && (
                <div className="absolute top-3 right-3 z-10">
                  <Badge variant="destructive" className="flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Condition Expired
                  </Badge>
                </div>
              )}
              {policy.expiryReason === 'renewal_due' && (
                <div className="absolute top-3 right-3 z-10">
                  <Badge variant="secondary" className="flex items-center gap-1 bg-orange-100 text-orange-800">
                    <Clock className="w-3 h-3" />
                    Renewal in {policy.daysUntilExpiry} days
                  </Badge>
                </div>
              )}
              
              <PolicyLibraryCard
                policy={policy}
                userRoles={roles || []}
              />
            </Card>
          ))
        )}
      </div>

      {!loading && filteredPolicies.length === 0 && (
        <div className="text-center py-12">
          <AlertIcon className="mx-auto h-12 w-12 text-orange-600 mb-4" />
          <h3 className="text-lg font-semibold text-muted-foreground mb-2">
            {policies.length === 0 ? "All Clear!" : "No policies match your filters"}
          </h3>
          <p className="text-sm text-muted-foreground">
            {policies.length === 0 
              ? "No policies with expired conditions or upcoming renewals."
              : "Try adjusting your search or filter criteria."}
          </p>
        </div>
      )}
    </div>
  );
};

export default PolicyExpiredRenewals;
